import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/config";
import { useContent, C } from "@/hooks/useContent";
import { Check, Loader2, ArrowRight, ArrowLeft, CalendarDays, MapPin, Clock, Sparkles, Info } from "lucide-react";
import { format, parseISO } from "date-fns";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const STEPS = ["Property", "Date & time", "Contact", "Confirm"];

function fmtDay(iso) {
  try { return format(parseISO(iso), "EEE, MMM d"); } catch { return iso; }
}

function fmtSlot(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return format(d, "h:mm a");
}

export default function BookingPage() {
  const content = useContent();
  useDocumentMeta({
    title: "Book a Free Estimate · Northwestern Exterior Cleaning",
    description: "Book a free 30-minute on-site estimate visit. Marlowe measures your property and quotes in person — no obligation, North Seattle service.",
    path: "/booking",
  });
  const navigate = useNavigate();

  const [step, setStep] = useState(0);

  // Step 1 — property
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");

  // Step 2 — date & slot
  const [daysData, setDaysData] = useState(null);   // { days: [{date, available, open_slots, reason}] }
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);           // [{time, available}]
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingDays, setLoadingDays] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Step 3 — contact
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  // Load 30 days of availability whenever step 2 becomes active
  const loadDays = useCallback(async () => {
    setLoadingDays(true);
    try {
      const { data } = await api.get("/bookings/days?days=30");
      setDaysData(data);
    } catch (e) {
      toast.error("Couldn't load available days. Try again.");
    } finally {
      setLoadingDays(false);
    }
  }, []);

  const loadSlots = useCallback(async (date) => {
    setLoadingSlots(true);
    setSlots([]);
    setSelectedSlot(null);
    try {
      const { data } = await api.get(`/bookings/slots?date=${date}`);
      setSlots(data.slots || []);
    } catch (e) {
      toast.error("Couldn't load slots for that day.");
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (step === 1 && !daysData) loadDays();
  }, [step, daysData, loadDays]);

  const canNextFromStep = useMemo(() => {
    if (step === 0) return address.trim().length >= 4;
    if (step === 1) return !!selectedDate && !!selectedSlot;
    if (step === 2) {
      const emailOk = /\S+@\S+\.\S+/.test(customerEmail);
      return customerName.trim().length >= 2 && emailOk && customerPhone.trim().length >= 7;
    }
    return true;
  }, [step, address, selectedDate, selectedSlot, customerName, customerEmail, customerPhone]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        customer_name: customerName.trim(),
        customer_email: customerEmail.trim(),
        customer_phone: customerPhone.trim(),
        address: address.trim(),
        date: selectedDate,
        time_slot: selectedSlot,
        notes: notes.trim(),
      });
      setConfirmed(data);
      setStep(4);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (step === 3) return submit();
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  // ----- CONFIRMED SCREEN -----
  if (step === 4 && confirmed) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16" data-testid="booking-confirmed">
        <Card className="p-8 sm:p-12 text-center border-border">
          <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
            <Check className="w-8 h-8" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tighter mt-6">
            {C(content, "booking.confirmed_heading", "Estimate visit booked")}
          </h1>
          <p className="mt-3 text-muted-foreground text-base max-w-md mx-auto" data-testid="booking-confirmed-body">
            {C(content, "booking.confirmed_body", "Marlowe will arrive at your address on the date and time below to measure and quote in person. No obligation — the visit is free.")}
          </p>
          <div className="mt-8 inline-flex flex-col gap-2 items-start text-left bg-muted/60 rounded-2xl p-5 min-w-[280px]">
            <div className="flex items-center gap-2 text-sm"><CalendarDays className="w-4 h-4 text-primary" /> <strong>{fmtDay(confirmed.date)}</strong> at <strong>{fmtSlot(confirmed.time_slot)}</strong></div>
            <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-primary" /> {confirmed.address}</div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Clock className="w-4 h-4" /> 30-minute visit</div>
          </div>
          <p className="mt-8 text-xs text-muted-foreground">A calendar invite is on its way to <strong>{confirmed.customer_email}</strong>.</p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate("/")} data-testid="booking-home-btn">Back to home</Button>
            <Button onClick={() => navigate("/portfolio")} data-testid="booking-portfolio-btn">See recent work</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 sm:py-16" data-testid="booking-page">
      {/* Header */}
      <div className="text-center mb-10">
        <p className="overline text-muted-foreground">{C(content, "booking.eyebrow", "Free estimate · no obligation")}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
          {C(content, "booking.heading", "Book a free on-site estimate")}
        </h1>
        <p className="mt-4 text-muted-foreground text-base max-w-xl mx-auto">
          {C(content, "booking.subheading", "Pick a 30-minute window that works. Marlowe measures the property and gives you a firm quote in person.")}
        </p>
      </div>

      {/* Progress */}
      <div className="flex items-center justify-center gap-1 sm:gap-2 mb-8 text-[11px] sm:text-xs flex-wrap" data-testid="booking-progress">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-1.5 sm:gap-2">
            <div
              className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center font-semibold text-[10px] sm:text-[11px] flex-shrink-0 ${
                i < step ? "bg-primary text-primary-foreground" :
                i === step ? "border-2 border-primary text-primary" :
                "border border-border text-muted-foreground"
              }`}
              data-testid={`booking-progress-step-${i}`}
            >
              {i < step ? <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> : i + 1}
            </div>
            <span className={i === step ? "font-medium" : "text-muted-foreground"}>{label}</span>
            {i < STEPS.length - 1 && <span className="text-muted-foreground/40 mx-0.5 sm:mx-1 hidden sm:inline">·</span>}
          </div>
        ))}
      </div>

      <Card className="p-6 sm:p-8 border-border">
        {/* STEP 0 — Property */}
        {step === 0 && (
          <div className="space-y-6" data-testid="booking-step-property">
            <div>
              <Label htmlFor="address" className="text-sm font-medium">{C(content, "booking.address_label", "Address")}</Label>
              <p className="text-xs text-muted-foreground mt-1">{C(content, "booking.address_hint", "The street address Marlowe should visit.")}</p>
              <Input
                id="address"
                type="text"
                data-testid="booking-address-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, Seattle, WA"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="notes" className="text-sm font-medium">{C(content, "booking.notes_label", "What surfaces need cleaning?")}</Label>
              <p className="text-xs text-muted-foreground mt-1">{C(content, "booking.notes_prompt", "Optional — describe the surfaces (e.g. \"concrete driveway and brick walkway\") so Marlowe knows what tools to bring.")}</p>
              <Textarea
                id="notes"
                data-testid="booking-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder='e.g. "Concrete driveway and brick patio. Some oil stains near the garage."'
                rows={5}
                className="mt-2"
              />
            </div>
          </div>
        )}

        {/* STEP 1 — Date + Slot */}
        {step === 1 && (
          <div data-testid="booking-step-date">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="w-4 h-4 text-primary" />
              <h2 className="font-display text-lg font-semibold">{C(content, "booking.date_heading", "Pick a day")}</h2>
            </div>
            {loadingDays && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading availability…</div>}
            {daysData && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="booking-days-grid">
                {daysData.days.filter((d) => d.available).slice(0, 21).map((d) => (
                  <button
                    key={d.date}
                    onClick={() => { setSelectedDate(d.date); loadSlots(d.date); }}
                    data-testid={`booking-day-${d.date}`}
                    className={`text-sm rounded-xl border p-3 text-left transition ${
                      selectedDate === d.date
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border hover:border-primary/50 hover:bg-muted/50"
                    }`}
                  >
                    <div className="font-medium">{fmtDay(d.date)}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{d.open_slots} slot{d.open_slots === 1 ? "" : "s"}</div>
                  </button>
                ))}
              </div>
            )}
            {daysData && daysData.days.filter((d) => d.available).length === 0 && !loadingDays && (
              <p className="text-sm text-muted-foreground">No available days in the next 30 — please call Marlowe.</p>
            )}

            {selectedDate && (
              <div className="mt-8" data-testid="booking-slots-section">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-primary" />
                  <h3 className="font-display text-base font-semibold">{C(content, "booking.slot_heading", "Pick a 30-minute window")}</h3>
                </div>
                {loadingSlots && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading slots…</div>}
                {!loadingSlots && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-slots-grid">
                    {slots.map((s) => (
                      <button
                        key={s.time}
                        disabled={!s.available}
                        onClick={() => setSelectedSlot(s.time)}
                        data-testid={`booking-slot-${s.time}`}
                        className={`text-sm rounded-lg border py-2.5 transition ${
                          !s.available
                            ? "border-border bg-muted/40 text-muted-foreground/50 cursor-not-allowed line-through"
                            : selectedSlot === s.time
                            ? "border-primary bg-primary text-primary-foreground shadow-sm"
                            : "border-border hover:border-primary/50 hover:bg-muted/50"
                        }`}
                      >
                        {fmtSlot(s.time)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — Contact */}
        {step === 2 && (
          <div className="space-y-5" data-testid="booking-step-contact">
            <div>
              <Label htmlFor="customer-name" className="text-sm font-medium">Full name</Label>
              <Input
                id="customer-name"
                type="text"
                data-testid="booking-name-input"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="customer-email" className="text-sm font-medium">Email</Label>
              <p className="text-xs text-muted-foreground mt-1">We&apos;ll send a calendar invite here.</p>
              <Input
                id="customer-email"
                type="email"
                data-testid="booking-email-input"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="jane@example.com"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="customer-phone" className="text-sm font-medium">Phone</Label>
              <p className="text-xs text-muted-foreground mt-1">Marlowe may text if he&apos;s running early or late.</p>
              <Input
                id="customer-phone"
                type="tel"
                data-testid="booking-phone-input"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="(206) 555-0123"
                className="mt-2"
              />
            </div>
          </div>
        )}

        {/* STEP 3 — Confirm */}
        {step === 3 && (
          <div data-testid="booking-step-confirm">
            <h2 className="font-display text-xl font-semibold tracking-tight mb-4">{C(content, "booking.confirm_heading", "Review your visit")}</h2>
            <dl className="grid gap-4 text-sm">
              <div className="flex justify-between gap-4 border-b border-border pb-3">
                <dt className="text-muted-foreground">When</dt>
                <dd className="text-right font-medium">{fmtDay(selectedDate)} · {fmtSlot(selectedSlot)}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-border pb-3">
                <dt className="text-muted-foreground">Duration</dt>
                <dd className="text-right">30 minutes (free estimate visit)</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-border pb-3">
                <dt className="text-muted-foreground">Address</dt>
                <dd className="text-right">{address}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-border pb-3">
                <dt className="text-muted-foreground">Contact</dt>
                <dd className="text-right">
                  {customerName}<br />
                  <span className="text-muted-foreground">{customerEmail}</span><br />
                  <span className="text-muted-foreground">{customerPhone}</span>
                </dd>
              </div>
              {notes && (
                <div className="flex justify-between gap-4 border-b border-border pb-3">
                  <dt className="text-muted-foreground">Notes</dt>
                  <dd className="text-right max-w-sm">{notes}</dd>
                </div>
              )}
            </dl>
            <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground bg-muted/60 rounded-xl p-3">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary" />
              <span>{C(content, "booking.confirm_note", "Marlowe will confirm the final price on-site after a quick measurement. No obligation to book the wash.")}</span>
            </div>
          </div>
        )}
      </Card>

      {/* Nav */}
      <div className="flex justify-between mt-6">
        <Button
          variant="ghost"
          onClick={goBack}
          disabled={step === 0}
          data-testid="booking-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={goNext}
          disabled={!canNextFromStep || submitting}
          data-testid="booking-next-btn"
          className="rounded-full px-6"
        >
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Booking…</> :
            step === 3 ? <>Confirm visit <Sparkles className="w-4 h-4 ml-2" /></> :
            <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
      </div>
    </div>
  );
}
